import { findRow, formatPrice } from "@/data/pricing";
import { specialisationFaqs } from "@/data/specialisationFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import { m } from "framer-motion";
import { ArrowRight, Receipt } from "lucide-react";
import {
  canonicalLinks,
  ogUrl,
  jsonLd,
  serviceSchema,
  breadcrumbSchema,
  faqPageSchema,
  webPageSchema, absoluteUrl, ogImage } from "@/lib/seo";
import { PhotoBand } from "@/components/site/PhotoBand";
import warehouseBayImg from "@/assets/photos/warehouse-bay.webp";
import { NextStep } from "@/components/site/NextStep";
import { DeliveredThrough } from "@/components/site/CrossLinks";

const answerPrice = (name: string) => formatPrice(findRow(name));

const PATH = "/specialisations/procurement-and-supplier-costs";
const TITLE =
  "Procurement and supplier cost reduction for SMEs";
const DESCRIPTION =
  "Supplier consolidation and renegotiation, software rationalisation, spend visibility and make-versus-buy on recurring services, run as a periodic discipline.";

const CAREER_NOTE =
  "These results come from Adam's operating career before Ajility, inside the businesses themselves.";

const proof = [
  {
    figure: "A 70% software cost saving from replatforming a project-management stack",
    org: "Senior operating role",
  },
  {
    figure: "A $150K annual IT cost reduction",
    org: "Senior operating role",
  },
  {
    figure: "Leadership of a $500K spend-reduction project",
    org: "Senior operating role",
  },
];

const covers = [
  {
    title: "Spend visibility",
    desc: "Every recurring cost in one list, by supplier, by category, by owner and by renewal date, before anything is negotiated.",
  },
  {
    title: "Supplier consolidation",
    desc: "Where the same category is bought from four suppliers on four sets of terms, the volume is worth more in one place than in four.",
  },
  {
    title: "Renegotiation",
    desc: "A prepared position per supplier: current spend, market position, what is being asked for and what is being offered in return.",
  },
  {
    title: "Software and subscriptions",
    desc: "Licences counted against actual users, overlapping tools identified, unused seats removed and renewal dates put on a calendar.",
  },
  {
    title: "Make versus buy",
    desc: "For recurring services, an honest comparison of the outsourced cost against the internal cost, including the management time it consumes.",
  },
  {
    title: "The annual rhythm",
    desc: "A fixed date each year to review the spend list and the renewals, so pricing is tested rather than accepted by default.",
  },
];

const faqs = specialisationFaqs["procurement-and-supplier-costs"];

export const Route = createFileRoute("/specialisations/procurement-and-supplier-costs")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("specialisations-procurement-and-supplier-costs", `${TITLE} by Ajility`),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(webPageSchema({ mainEntityId: `${absoluteUrl(PATH)}#service`, path: PATH, name: `${TITLE} | Ajility`, description: DESCRIPTION })),
      jsonLd(
        breadcrumbSchema([
          { name: "Expertise", path: "/specialisations" },
          { name: "Procurement and supplier costs", path: PATH },
        ]),
      ),
      jsonLd(
        serviceSchema({
          name: "Procurement and supplier cost reduction",
          description: DESCRIPTION,
          path: PATH,
          slug: "procurement-and-supplier-costs",
          serviceType: "Procurement and supplier cost reduction",
        }),
      ),
      jsonLd(faqPageSchema(PATH, faqs, `${absoluteUrl(PATH)}#service`)),
    ],
  }),
  component: ProcurementSpecialisation,
});

function ProcurementSpecialisation() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-7xl px-5 sm:px-8"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-steel-on-dark">
            <Receipt className="h-6 w-6" aria-hidden="true" />
          </div>
          <span className="eyebrow mt-8 block on-dark">Expertise: procurement and supplier costs</span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            Your suppliers reprice every year. Most businesses never reprice back.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-frost/75 text-pretty">
            {`Ajility runs supplier and subscription reviews as a periodic discipline, starting with a spend list that sizes the addressable base before any commitment. Where cost is one of several questions, start with the Operating Diagnostic, ${answerPrice("Operating Diagnostic")}.`}{" "}
            This is not a procurement department and it is not a permanent function. It is a
            periodic discipline: <Link to="/insights/$slug" params={{ slug: "mining-90-days" }} className="underline decoration-white/40 underline-offset-4 hover:decoration-frost">see the whole recurring spend</Link>, consolidate where volume is
            fragmented, renegotiate what has never been tested, and put the renewal dates in a
            calendar so next year is a decision rather than a default.
          </p>
          <div className="mt-10 flex flex-wrap items-start gap-4">
            <BookFitCall
              placement="specialisations_procurement_supplier_hero"
              buttonClassName="group inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
            </BookFitCall>
            <Link
              to="/packages"
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-frost/85 transition hover:border-white/30 hover:text-frost"
            >
              See one process, one fix
            </Link>
          </div>
        </m.div>
      </section>

      <PhotoBand
        src={warehouseBayImg}
        alt="Stacked pallets and racking in a warehouse dispatch bay"
        priority
      />

      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">What the work covers</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Six ways recurring cost is taken back
          </h2>
          <p className="mt-4 max-w-3xl text-charcoal">
            Cost reduction here means paying a fair price for things the business still needs, not
            cutting capability. Every change is made with the category owner, and anything that
            would put delivery or safety at risk is left alone.
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {covers.map((c, i) => (
              <m.div
                key={c.title}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <h3 className="font-serif text-xl text-foreground">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{c.desc}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">Why it drifts</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">
            Nobody signs up for a worse deal. It arrives one renewal at a time.
          </h2>
          <p className="mt-6 leading-relaxed text-charcoal">
            A supplier arrangement is usually negotiated once, at a size the business has long since
            outgrown, and then renewed automatically. Software is bought by whoever needed it, at
            the moment they needed it, and the seat count only ever goes up. No single renewal is
            large enough to escalate, so the whole base is never reviewed at once.
          </p>
          <p className="mt-4 leading-relaxed text-charcoal">
            The discipline is simply to look at the entire recurring spend in one sitting, with the
            renewal dates attached, and to decide deliberately which arrangements are tested this
            year. It takes a few weeks and it holds for a year.
          </p>
        </div>
      </section>

      <section className="bg-midnight py-24 text-frost">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow on-dark">Track record</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-balance">
            Cost work delivered in senior operating roles
          </h2>
          <p className="mt-4 max-w-3xl text-steel-on-dark">{CAREER_NOTE}</p>
          <ul className="mt-12 grid gap-6 md:grid-cols-2">
            {proof.map((p) => (
              <li key={p.figure} className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
                <p className="font-serif text-lg leading-snug text-frost">{p.figure}</p>
                <p className="mt-3 text-sm text-steel-on-dark">{p.org}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <span className="eyebrow">Where this is bought</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">One process, one fix</h2>
          <p className="mt-4 text-charcoal">
            A supplier and subscription review is commonly run as one process, one fix, or scoped
            inside the Operating Diagnostic where cost is one of several questions. Both are
            published, with their scope and fee, on the packages page.
          </p>
          <Link
            to="/packages"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-midnight-deep"
          >
            See one process, one fix and the Operating Diagnostic
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
          <dl className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="card-panel">
                <dt>
                  <h3 className="font-serif text-lg text-foreground">{f.q}</h3>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-charcoal">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <DeliveredThrough slug="procurement-and-supplier-costs" />

      <NextStep
        heading="Start with the free Operating Scan"
        body="21 plain statements about how the business actually runs, answered from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("specialisations_procurement_supplier_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
